import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Stethoscope, IndianRupee, CheckCircle2, Clock4 } from "lucide-react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Public, browse-only directory of approved care providers. Patients can see
 * the professional's profile and availability, but they CAN ONLY book through
 * an admin-approved referral. The "Request referral" button opens a dialog and
 * creates a Referral row in the backend.
 */
export default function CareProviders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pros, setPros] = useState([]);
  const [myReferrals, setMyReferrals] = useState([]);
  const [requestOn, setRequestOn] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/professionals");
      setPros(data);
      if (user?.role === "patient") {
        const { data: refs } = await api.get("/referrals/my");
        setMyReferrals(refs);
      }
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Build map: professional_id → most-relevant referral status for this patient
  const referralStatus = useMemo(() => {
    const m = {};
    for (const r of myReferrals) {
      const cur = m[r.professional_id];
      // Priority: approved > pending > used > denied (so patient sees the best status)
      const rank = { approved: 4, pending: 3, used: 2, denied: 1 };
      if (!cur || (rank[r.status] || 0) > (rank[cur.status] || 0)) m[r.professional_id] = r;
    }
    return m;
  }, [myReferrals]);

  const openRequest = (pro) => {
    if (!user) {
      toast.error("Please log in to request a referral.");
      navigate("/login", { state: { from: "/care-providers" } });
      return;
    }
    setRequestOn(pro);
    setReason("");
  };

  const submitRequest = async () => {
    if (!requestOn) return;
    setSaving(true);
    try {
      await api.post("/referrals", { professional_id: requestOn.id, reason });
      toast.success("Referral request sent — we'll email you once it's approved.");
      setRequestOn(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12" data-testid="care-providers-page">
      <h1 className="font-heading text-3xl md:text-4xl text-slate-900 flex items-center gap-2">
        <Stethoscope className="w-7 h-7 text-[#5E8B7E]" /> Our care providers
      </h1>
      <p className="text-sm text-slate-500 mt-2 max-w-2xl">
        Dr. Prathusha works with a curated team of dietitians, fellow doctors and allied specialists.
        To consult any of them, please <b>request a referral below</b>. Once Dr. Prathusha's team
        approves it, you'll receive an email with a direct booking link.
      </p>

      {pros.length === 0 ? (
        <div data-testid="care-providers-empty" className="bg-white rounded-2xl border border-dashed border-[#E5EAE8] p-12 text-center text-sm text-slate-500 mt-10">
          No care providers are listed right now. Please check back soon.
        </div>
      ) : (
        <div className="space-y-4 mt-8">
          {pros.map((p) => {
            const ref = referralStatus[p.id];
            return <ProviderCard key={p.id} pro={p} referral={ref} onRequestReferral={() => openRequest(p)} />;
          })}
        </div>
      )}

      <Dialog open={!!requestOn} onOpenChange={(o) => !o && setRequestOn(null)}>
        <DialogContent data-testid="referral-request-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request a referral to {requestOn?.name}</DialogTitle>
            <DialogDescription>
              Dr. Prathusha's team will review your request and notify you by email. Once approved,
              you'll be able to book a slot directly.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="referral-reason">Why would you like this consultation? (optional)</Label>
            <Textarea
              id="referral-reason"
              data-testid="referral-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. 'PCOS diet plan after Dr. Prathusha's recommendation'"
              rows={4}
              className="mt-1.5 rounded-xl"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestOn(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="referral-submit-btn" disabled={saving} onClick={submitRequest} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">
              {saving ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProviderCard({ pro, referral, onRequestReferral }) {
  const [availability, setAvailability] = useState([]);
  const [showSlots, setShowSlots] = useState(false);

  // The public availability endpoint exposes slot times per date, not the weekly schedule.
  // Show the schedule windows by fetching availability for the next 7 days and merging.
  const loadWeekly = useCallback(async () => {
    if (showSlots) return;
    setShowSlots(true);
    const today = new Date();
    const seen = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      try {
        const { data } = await api.get(`/professionals/${pro.id}/availability`, { params: { date: iso } });
        if (data.slots && data.slots.length) {
          const wd = (d.getDay() + 6) % 7; // 0 = Monday
          seen[wd] = { day: WEEKDAYS[wd], times: data.slots.filter((s) => s.available).map((s) => s.time) };
        }
      } catch {/* ignored */}
    }
    setAvailability(Object.values(seen));
  }, [pro.id, showSlots]);

  return (
    <div
      data-testid={`care-pro-card-${pro.id}`}
      className="bg-white rounded-2xl border border-[#EDF1EF] p-5 sm:p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-xl text-slate-900">{pro.name}</h3>
            <Badge className="bg-[#D9E4E1] text-[#4A7065] rounded-full text-xs">{pro.specialty}</Badge>
          </div>
          {pro.qualification && <p className="text-sm text-slate-600 mt-1">{pro.qualification}</p>}
          {pro.bio && <p className="text-sm text-slate-600 mt-3">{pro.bio}</p>}
          <p className="text-sm text-slate-700 mt-3 inline-flex items-center gap-1">
            <IndianRupee className="w-3.5 h-3.5" /> <b>{pro.consultation_fee || 0}</b> per consultation
          </p>
          <button
            data-testid={`care-pro-availability-${pro.id}`}
            onClick={loadWeekly}
            className="block mt-3 text-xs text-[#2C557E] hover:underline"
          >
            {showSlots ? "Availability shown below" : "See available slots →"}
          </button>
          {showSlots && (
            <div className="mt-3 bg-[#F4F7F6] rounded-xl p-3 text-xs text-slate-600">
              {availability.length === 0 ? (
                <p data-testid={`care-pro-noavail-${pro.id}`}>No upcoming availability in the next week.</p>
              ) : (
                <ul className="space-y-1.5">
                  {availability.map((d) => (
                    <li key={d.day} className="flex items-start gap-2">
                      <Clock4 className="w-3.5 h-3.5 text-[#5E8B7E] mt-0.5" />
                      <span><b>{d.day}:</b> {d.times.slice(0, 6).join(", ")}{d.times.length > 6 ? ` … +${d.times.length - 6} more` : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="shrink-0">
          {referral?.status === "approved" ? (
            <Link to={`/book-with-professional?pro=${pro.id}`}>
              <Button data-testid={`care-pro-book-${pro.id}`} className="rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Book — referral approved
              </Button>
            </Link>
          ) : referral?.status === "pending" ? (
            <Button data-testid={`care-pro-pending-${pro.id}`} variant="outline" disabled className="rounded-full text-amber-700 border-amber-200">
              Awaiting approval
            </Button>
          ) : referral?.status === "used" ? (
            <Badge className="bg-slate-100 text-slate-600 rounded-full" data-testid={`care-pro-used-${pro.id}`}>
              Already consulted
            </Badge>
          ) : (
            <Button
              data-testid={`care-pro-request-${pro.id}`}
              onClick={onRequestReferral}
              variant="outline"
              className="rounded-full border-[#5E8B7E] text-[#4A7065] hover:bg-[#D9E4E1]"
            >
              Request referral
            </Button>
          )}
        </div>
      </div>
      {referral?.status === "denied" && referral?.decision_note && (
        <p className="mt-3 text-xs text-red-700 bg-red-50 rounded-lg p-2.5">
          Last request was not approved. Clinic note: {referral.decision_note}
        </p>
      )}
    </div>
  );
}
